package com.evoapps.evoshift.ui.join

import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.login.AuthScreen
import com.evoapps.evoshift.viewmodels.AuthEvent
import com.evoapps.evoshift.viewmodels.AuthViewModel
import com.evoapps.evoshift.viewmodels.JoinTeamState
import com.evoapps.evoshift.viewmodels.JoinTeamViewModel
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.join_team_error
import evoshift.composeapp.generated.resources.join_team_go_to_dashboard
import evoshift.composeapp.generated.resources.join_team_invited
import evoshift.composeapp.generated.resources.join_team_invited_by
import evoshift.composeapp.generated.resources.join_team_join
import evoshift.composeapp.generated.resources.join_team_verifying
import evoshift.composeapp.generated.resources.join_team_workplace
import org.jetbrains.compose.resources.stringResource
import com.evoapps.evoshift.di.diViewModel

@Composable
fun JoinTeamScreen(
    inviteCode: String,
    viewModel: JoinTeamViewModel,
    onNavigateToDashboard: () -> Unit
) {
    val state by viewModel.state.collectAsState()
    val authViewModel: AuthViewModel = diViewModel()

    // Trigger validation when screen opens
    LaunchedEffect(inviteCode) {
        viewModel.validateInvite(inviteCode)
    }

    LaunchedEffect(state) {
        if (state is JoinTeamState.Success) {
            onNavigateToDashboard()
        }
    }

    val s = state
    if (s is JoinTeamState.InviteDetailsLoaded && !s.isUserLoggedIn) {
        // User not logged in - show AuthScreen with special header
        LaunchedEffect(Unit) {
            // Force registration mode if invited
            if (authViewModel.uiState.value.isLoginMode) {
                authViewModel.onEvent(AuthEvent.ToggleMode)
            }
        }

        AuthScreen(
            viewModel = authViewModel,
            headerContent = {
                InvitationCard(
                    workplaceName = s.workplaceName,
                    inviterName = s.inviterName,
                    modifier = Modifier.padding(bottom = 24.dp)
                )
            },
            onNavigateToDashboard = {
                // After login/register, the user is still on this JoinTeam route.
                // We need to reload the state to detect they are logged in now.
                viewModel.validateInvite(inviteCode)
            }
        )
    } else {
        Column(
            modifier = Modifier.fillMaxSize().contentWidth(FormMaxWidth).padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            when (s) {
                is JoinTeamState.Loading -> {
                    CircularProgressIndicator()
                    Spacer(Modifier.height(16.dp))
                    Text(stringResource(Res.string.join_team_verifying))
                }

                is JoinTeamState.InviteDetailsLoaded -> {
                    // Standard accept invite screen (logged in)
                    Text(
                        stringResource(Res.string.join_team_invited),
                        style = MaterialTheme.typography.headlineMedium
                    )
                    Spacer(Modifier.height(24.dp))

                    InvitationCard(s.workplaceName, s.inviterName)

                    Spacer(Modifier.height(32.dp))

                    Button(
                        onClick = { viewModel.acceptInvite(inviteCode) },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(stringResource(Res.string.join_team_join))
                    }
                }

                is JoinTeamState.Error -> {
                    Text(
                        stringResource(Res.string.join_team_error),
                        style = MaterialTheme.typography.headlineMedium,
                        color = MaterialTheme.colorScheme.error
                    )
                    Spacer(Modifier.height(16.dp))
                    Text(
                        s.message.asString(),
                        textAlign = TextAlign.Center,
                        style = MaterialTheme.typography.bodyLarge
                    )
                    Spacer(Modifier.height(24.dp))
                    Button(onClick = onNavigateToDashboard) {
                        Text(stringResource(Res.string.join_team_go_to_dashboard))
                    }
                }

                is JoinTeamState.Success -> {
                    // Handled by LaunchedEffect
                }

                JoinTeamState.Idle -> {
                    // Initial state
                }
            }
        }
    }
}

@Composable
fun InvitationCard(
    workplaceName: String,
    inviterName: String,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.primaryContainer
        )
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                stringResource(Res.string.join_team_invited),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            Spacer(Modifier.height(8.dp))
            Text(
                stringResource(Res.string.join_team_workplace),
                style = MaterialTheme.typography.labelSmall
            )
            Text(
                workplaceName,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )
            Spacer(Modifier.height(8.dp))
            Text(
                stringResource(Res.string.join_team_invited_by),
                style = MaterialTheme.typography.labelSmall
            )
            Text(inviterName, style = MaterialTheme.typography.bodyMedium)
        }
    }
}
